import { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, FileText, Edit2, Trash2, Star, Copy, Eye, Variable, Image,
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Heading1, Heading2, Heading3, Palette, Type
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATE_VARIABLES = [
  { key: '{{company_logo}}', label: 'Company Logo', description: 'Your company logo image' },
  { key: '{{client_name}}', label: 'Client Name', description: 'Full name of the client' },
  { key: '{{client_email}}', label: 'Client Email', description: 'Client email address' },
  { key: '{{client_phone}}', label: 'Client Phone', description: 'Client phone number' },
  { key: '{{event_date}}', label: 'Event Date', description: 'Date of the event' },
  { key: '{{start_time}}', label: 'Start Time', description: 'Event start time' },
  { key: '{{end_time}}', label: 'End Time', description: 'Event end time' },
  { key: '{{event_type}}', label: 'Event Type', description: 'Type of event (Wedding, Corporate, etc.)' },
  { key: '{{venue}}', label: 'Venue', description: 'Event venue name' },
  { key: '{{package_name}}', label: 'Package Name', description: 'Selected package name' },
  { key: '{{package_price}}', label: 'Package Price', description: 'Package price amount' },
  { key: '{{total_amount}}', label: 'Total Amount', description: 'Total contract amount' },
  { key: '{{deposit_amount}}', label: 'Deposit/Retainer Amount', description: 'Required deposit/retainer' },
  { key: '{{retainer_amount}}', label: 'Retainer Amount', description: 'Initial retainer amount' },
  { key: '{{retainer_due_date}}', label: 'Retainer Due Date', description: 'Date retainer is due' },
  { key: '{{payment_schedule}}', label: 'Payment Schedule', description: 'Full payment schedule with all installments' },
  { key: '{{payment_schedule_table}}', label: 'Payment Schedule Table', description: 'Formatted table of all scheduled payments' },
  { key: '{{discounts}}', label: 'Discounts Applied', description: 'List of applied discounts with amounts' },
  { key: '{{discount_tags}}', label: 'Discount Tags', description: 'Tags from applied discounts (e.g., "Wedding Special")' },
  { key: '{{company_name}}', label: 'Company Name', description: 'Your DJ company name' },
  { key: '{{company_phone}}', label: 'Company Phone', description: 'Your company phone' },
  { key: '{{company_email}}', label: 'Company Email', description: 'Your company email' },
  { key: '{{today_date}}', label: 'Today\'s Date', description: 'Current date' },
  // New financial tags
  { key: '{{financial_summary}}', label: '📊 Financial Summary', description: 'Complete financial breakdown: Package, Add-ons, Discounts, Total & Payment Schedule (styled HTML table)' },
  { key: '{{package_details}}', label: 'Package Details', description: 'Package name and price in styled table format' },
  { key: '{{addons_list}}', label: 'Add-ons List', description: 'List of selected add-ons with prices' },
  { key: '{{discounts_list}}', label: 'Discounts List', description: 'Styled list of applied discounts' },
  { key: '{{payment_schedule_styled}}', label: 'Payment Schedule (Styled)', description: 'Beautiful styled payment schedule table' },
];

const TEMPLATE_CATEGORIES = ['General', 'Wedding', 'Corporate', 'Party', 'Club'];

const DEFAULT_TEMPLATE = `<div style="text-align: center;">
<p><span style="color: #888;">[Company Logo - Use Insert Logo button]</span></p>
<h1 style="color: #06b6d4;">DJ SERVICES AGREEMENT</h1>
</div>

<p>This Agreement is entered into on <strong>{{today_date}}</strong> between:</p>

<h3 style="color: #8b5cf6;">SERVICE PROVIDER:</h3>
<p><strong>{{company_name}}</strong><br>
Phone: {{company_phone}}<br>
Email: {{company_email}}</p>

<h3 style="color: #8b5cf6;">CLIENT:</h3>
<p><strong>{{client_name}}</strong><br>
Phone: {{client_phone}}<br>
Email: {{client_email}}</p>

<h3 style="color: #8b5cf6;">EVENT DETAILS:</h3>
<ul>
<li><strong>Event Type:</strong> {{event_type}}</li>
<li><strong>Event Date:</strong> {{event_date}}</li>
<li><strong>Time:</strong> {{start_time}} - {{end_time}}</li>
<li><strong>Venue:</strong> {{venue}}</li>
</ul>

<h3 style="color: #8b5cf6;">SERVICES &amp; PRICING:</h3>
<p><strong>Package:</strong> {{package_name}}<br>
<strong>Package Price:</strong> {{package_price}}<br>
<strong>Total Amount:</strong> {{total_amount}}<br>
<strong>Deposit Required:</strong> {{deposit_amount}}</p>

<h3 style="color: #8b5cf6;">TERMS AND CONDITIONS:</h3>
<ol>
<li><strong>DEPOSIT:</strong> A non-refundable deposit of {{deposit_amount}} is required to secure the date.</li>
<li><strong>PAYMENT:</strong> The remaining balance is due 14 days before the event date.</li>
<li><strong>CANCELLATION:</strong> Cancellation 30+ days before event: Deposit forfeited. Within 30 days: Full payment required.</li>
<li><strong>EQUIPMENT:</strong> Service provider will supply all necessary DJ equipment.</li>
<li><strong>SETUP TIME:</strong> Service provider requires minimum 1 hour for setup before event start.</li>
</ol>

<h3 style="color: #8b5cf6;">SIGNATURES:</h3>
<p><br></p>
<p>_______________________________ &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; _______________________________</p>
<p>Client Signature &amp; Date &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; DJ Signature &amp; Date</p>`;

// Rich Text Editor Toolbar Component
function EditorToolbar({ onCommand, onInsertColor }) {
  const colors = ['#000000', '#06b6d4', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b', '#10b981'];
  
  return (
    <div className="flex flex-wrap gap-1 p-2 bg-muted/30 rounded-t-lg border-b border-border">
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('bold')} title="Bold">
        <Bold className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('italic')} title="Italic">
        <Italic className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('underline')} title="Underline">
        <Underline className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('formatBlock', '<h1>')} title="Heading 1">
        <Heading1 className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('formatBlock', '<h2>')} title="Heading 2">
        <Heading2 className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('formatBlock', '<h3>')} title="Heading 3">
        <Heading3 className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('formatBlock', '<p>')} title="Paragraph">
        <Type className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('insertUnorderedList')} title="Bullet List">
        <List className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('insertOrderedList')} title="Numbered List">
        <ListOrdered className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('justifyLeft')} title="Align Left">
        <AlignLeft className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('justifyCenter')} title="Align Center">
        <AlignCenter className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('justifyRight')} title="Align Right">
        <AlignRight className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <div className="flex items-center gap-1">
        <Palette className="w-4 h-4 text-muted-foreground" />
        {colors.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => onInsertColor(color)}
            className="w-5 h-5 rounded border border-white/20 hover:scale-110 transition-transform"
            style={{ backgroundColor: color }}
            title={`Text color: ${color}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function ContractTemplatesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: DEFAULT_TEMPLATE,
    category: 'General'
  });
  
  const editorRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
    fetchSettings();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/contract-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  }, []);

  const insertColor = useCallback((color) => {
    document.execCommand('foreColor', false, color);
    editorRef.current?.focus();
  }, []);

  const handleEditorChange = useCallback(() => {
    if (editorRef.current) {
      setFormData(prev => ({ ...prev, content: editorRef.current.innerHTML }));
    }
  }, []);

  const handleCreate = async () => {
    if (!formData.name || !formData.content) {
      toast.error('Please fill in template name and content');
      return;
    }
    try {
      await axios.post(`${API}/contract-templates`, formData);
      toast.success('Template created successfully');
      setShowDialog(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.content) {
      toast.error('Please fill in template name and content');
      return;
    }
    try {
      await axios.put(`${API}/contract-templates/${selectedTemplate.id}`, formData);
      toast.success('Template updated successfully');
      setShowDialog(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await axios.delete(`${API}/contract-templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.put(`${API}/contract-templates/${id}/set-default`);
      toast.success('Default template set');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to set default template');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      await axios.post(`${API}/contract-templates`, {
        ...template,
        name: `${template.name} (Copy)`,
        is_default: false
      });
      toast.success('Template duplicated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      content: template.content,
      category: template.category || 'General'
    });
    setEditMode(true);
    setShowDialog(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setEditMode(false);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      content: DEFAULT_TEMPLATE,
      category: 'General'
    });
    setSelectedTemplate(null);
    setEditMode(false);
  };

  const insertVariable = (variable) => {
    if (editorRef.current) {
      document.execCommand('insertText', false, variable);
      handleEditorChange();
    }
    toast.success('Variable inserted');
  };

  const insertLogo = () => {
    if (settings?.logo) {
      if (editorRef.current) {
        document.execCommand('insertHTML', false, 
          `<img src="${settings.logo}" alt="Company Logo" style="max-width: 200px; max-height: 100px; display: block; margin: 0 auto;" />`
        );
        handleEditorChange();
      }
      toast.success('Logo inserted');
    } else {
      toast.error('No logo found. Please upload a logo in Settings first.');
    }
  };

  const previewTemplate = (template) => {
    setSelectedTemplate(template);
    setShowPreviewDialog(true);
  };

  const getPreviewContent = (content) => {
    let preview = content;
    const sampleData = {
      '{{company_logo}}': settings?.logo 
        ? `<img src="${settings.logo}" alt="Logo" style="max-width: 200px; max-height: 100px;" />`
        : '<span style="color: #888;">[Company Logo]</span>',
      '{{client_name}}': 'John Smith',
      '{{client_email}}': 'john.smith@email.com',
      '{{client_phone}}': '(555) 123-4567',
      '{{event_date}}': 'March 15, 2026',
      '{{start_time}}': '6:00 PM',
      '{{end_time}}': '11:00 PM',
      '{{event_type}}': 'Wedding Reception',
      '{{venue}}': 'Grand Ballroom Hotel',
      '{{package_name}}': 'Premium DJ Package',
      '{{package_price}}': '$2,500',
      '{{total_amount}}': '$2,500',
      '{{deposit_amount}}': '$500',
      '{{retainer_amount}}': '$500',
      '{{retainer_due_date}}': 'Upon signing',
      '{{payment_schedule}}': 'Retainer: $500.00 (Due: Upon signing)<br>Second Payment: $1,000.00 (Due: March 1, 2026)<br>Final Payment: $1,000.00 (Due: March 15, 2026)',
      '{{payment_schedule_table}}': `<table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
<thead><tr style="background: #f3f4f6;"><th style="padding: 10px; text-align: left; border-bottom: 2px solid #8b5cf6;">Description</th><th style="padding: 10px; text-align: left; border-bottom: 2px solid #8b5cf6;">Amount</th><th style="padding: 10px; text-align: left; border-bottom: 2px solid #8b5cf6;">Due Date</th></tr></thead>
<tbody><tr><td style="padding: 8px; border-bottom: 1px solid #ddd;">Retainer</td><td style="padding: 8px; border-bottom: 1px solid #ddd;">$500.00</td><td style="padding: 8px; border-bottom: 1px solid #ddd;">Upon signing</td></tr><tr><td style="padding: 8px; border-bottom: 1px solid #ddd;">Second Payment</td><td style="padding: 8px; border-bottom: 1px solid #ddd;">$1,000.00</td><td style="padding: 8px; border-bottom: 1px solid #ddd;">March 1, 2026</td></tr><tr><td style="padding: 8px; border-bottom: 1px solid #ddd;">Final Payment</td><td style="padding: 8px; border-bottom: 1px solid #ddd;">$1,000.00</td><td style="padding: 8px; border-bottom: 1px solid #ddd;">March 15, 2026</td></tr></tbody>
</table>`,
      '{{discounts}}': 'Early Bird (10%): -$250.00',
      '{{discount_tags}}': 'Early Bird, Wedding Special',
      '{{company_name}}': settings?.company_name || 'Your DJ Company',
      '{{company_phone}}': settings?.phone || '(555) 555-5555',
      '{{company_email}}': settings?.email || 'info@djcompany.com',
      '{{today_date}}': new Date().toLocaleDateString('en-US', { 
        year: 'numeric', month: 'long', day: 'numeric' 
      }),
      // New financial tags sample data
      '{{package_details}}': `<table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
<tr style="background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);">
<td style="padding: 12px; color: white; font-weight: bold; font-size: 14px;">PACKAGE</td>
<td style="padding: 12px; color: white; font-weight: bold; text-align: right; font-size: 14px;">PRICE</td>
</tr>
<tr>
<td style="padding: 12px; border-bottom: 1px solid #e5e7eb; font-size: 14px;">Premium DJ Package</td>
<td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: bold; font-size: 14px;">$2,500.00</td>
</tr>
</table>`,
      '{{addons_list}}': `<table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
<tr style="background: #f0fdf4;">
<td style="padding: 10px; font-weight: bold; color: #059669; font-size: 13px;">ADD-ONS</td>
<td style="padding: 10px; text-align: right;"></td>
</tr>
<tr>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; font-size: 13px;">Photo Booth</td>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; color: #059669; font-size: 13px;">+$350.00</td>
</tr>
<tr>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; font-size: 13px;">Uplighting</td>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; color: #059669; font-size: 13px;">+$200.00</td>
</tr>
</table>`,
      '{{discounts_list}}': `<table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
<tr style="background: #fef2f2;">
<td style="padding: 10px; font-weight: bold; color: #dc2626; font-size: 13px;">DISCOUNTS</td>
<td style="padding: 10px; text-align: right;"></td>
</tr>
<tr>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; font-size: 13px;">Early Bird (10%)</td>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; color: #dc2626; font-size: 13px;">-$250.00</td>
</tr>
</table>`,
      '{{payment_schedule_styled}}': `<table style="width: 100%; border-collapse: collapse; margin: 10px 0; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;">
<tr style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);">
<th style="padding: 12px; color: white; text-align: left; font-size: 13px;">PAYMENT SCHEDULE</th>
<th style="padding: 12px; color: white; text-align: center; font-size: 13px;">AMOUNT</th>
<th style="padding: 12px; color: white; text-align: right; font-size: 13px;">DUE DATE</th>
</tr>
<tr>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; font-size: 13px;"><strong>Retainer</strong></td>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: center; font-size: 13px;">$500.00</td>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-size: 13px;">Upon signing</td>
</tr>
<tr>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; font-size: 13px;">Final Payment</td>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: center; font-size: 13px;">$2,300.00</td>
<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-size: 13px;">March 10, 2026</td>
</tr>
</table>`,
      '{{financial_summary}}': `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 500px; margin: 20px 0;">
<table style="width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
<tr style="background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);">
<th style="padding: 14px; color: white; text-align: left; font-size: 14px; text-transform: uppercase; letter-spacing: 1px;">Package & Pricing</th>
<th style="padding: 14px; color: white; text-align: right; font-size: 14px;">Amount</th>
</tr>
<tr>
<td style="padding: 12px; font-size: 14px; border-bottom: 1px solid #e5e7eb;">Premium DJ Package</td>
<td style="padding: 12px; text-align: right; font-size: 14px; font-weight: 600; border-bottom: 1px solid #e5e7eb;">$2,500.00</td>
</tr>
<tr style="background: #f0fdf4;">
<td colspan="2" style="padding: 10px; font-weight: bold; color: #059669; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Add-ons</td>
</tr>
<tr>
<td style="padding: 8px 12px 8px 24px; font-size: 13px; border-bottom: 1px solid #f3f4f6;">Photo Booth</td>
<td style="padding: 8px 12px; text-align: right; color: #059669; font-size: 13px; border-bottom: 1px solid #f3f4f6;">+$350.00</td>
</tr>
<tr>
<td style="padding: 8px 12px 8px 24px; font-size: 13px; border-bottom: 1px solid #f3f4f6;">Uplighting</td>
<td style="padding: 8px 12px; text-align: right; color: #059669; font-size: 13px; border-bottom: 1px solid #f3f4f6;">+$200.00</td>
</tr>
<tr style="background: #fef2f2;">
<td colspan="2" style="padding: 10px; font-weight: bold; color: #dc2626; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Discounts</td>
</tr>
<tr>
<td style="padding: 8px 12px 8px 24px; font-size: 13px; border-bottom: 1px solid #f3f4f6;">Early Bird (10%)</td>
<td style="padding: 8px 12px; text-align: right; color: #dc2626; font-size: 13px; border-bottom: 1px solid #f3f4f6;">-$250.00</td>
</tr>
<tr style="background: #f9fafb;">
<td style="padding: 14px; font-weight: bold; font-size: 16px; border-top: 2px solid #8b5cf6;">TOTAL</td>
<td style="padding: 14px; text-align: right; font-weight: bold; font-size: 18px; color: #8b5cf6; border-top: 2px solid #8b5cf6;">$2,800.00</td>
</tr>
<tr style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);">
<th style="padding: 10px; color: white; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Payment Schedule</th>
<th style="padding: 10px; color: white; text-align: right; font-size: 12px;">Due Date</th>
</tr>
<tr>
<td style="padding: 10px 12px; font-size: 13px; border-bottom: 1px solid #e5e7eb;">Retainer <strong>$500.00</strong></td>
<td style="padding: 10px 12px; text-align: right; font-size: 13px; color: #6b7280; border-bottom: 1px solid #e5e7eb;">Upon signing</td>
</tr>
<tr>
<td style="padding: 10px 12px; font-size: 13px; border-bottom: 1px solid #e5e7eb;">Final Payment <strong>$2,300.00</strong></td>
<td style="padding: 10px 12px; text-align: right; font-size: 13px; color: #6b7280; border-bottom: 1px solid #e5e7eb;">March 10, 2026</td>
</tr>
</table>
</div>`,
    };
    
    Object.entries(sampleData).forEach(([key, value]) => {
      preview = preview.split(key).join(value);
    });
    
    return preview;
  };

  return (
    <div className="flex min-h-screen bg-obsidian" data-testid="contract-templates-page">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="templates-heading">
                Contract Templates
              </h1>
              <p className="text-muted-foreground">
                Create and manage reusable contract templates with rich formatting
              </p>
            </div>
            <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button 
                  onClick={openCreateDialog}
                  className="bg-neon-blue hover:bg-neon-blue/80 text-black" 
                  data-testid="create-template-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border border-border max-w-5xl max-h-[95vh] overflow-y-auto" data-testid="template-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-foreground">
                    {editMode ? 'Edit Template' : 'Create New Template'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Template Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-background border-border text-foreground"
                        placeholder="e.g., Wedding Contract"
                        data-testid="template-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Category</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                        <SelectTrigger className="bg-background border-border text-foreground" data-testid="template-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {TEMPLATE_CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-foreground">Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-background border-border text-foreground"
                      placeholder="Brief description of this template"
                      data-testid="template-description-input"
                    />
                  </div>

                  {/* Variables & Logo Panel */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-foreground">
                      <Variable className="w-4 h-4" />
                      Insert Variables & Logo
                    </Label>
                    <div className="flex flex-wrap gap-2 p-3 bg-muted/10 rounded-lg border border-border">
                      {settings?.logo && (
                        <button
                          type="button"
                          onClick={insertLogo}
                          className="px-3 py-1.5 text-xs rounded-full bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors flex items-center gap-1 font-medium"
                          title="Insert your company logo"
                          data-testid="insert-logo-btn"
                        >
                          <Image className="w-3 h-3" />
                          Insert Logo
                        </button>
                      )}
                      {TEMPLATE_VARIABLES.filter(v => v.key !== '{{company_logo}}').map((v) => (
                        <button
                          key={v.key}
                          type="button"
                          onClick={() => insertVariable(v.key)}
                          className="px-2 py-1 text-xs rounded-full bg-neon-purple/20 text-neon-purple hover:bg-neon-purple/30 transition-colors"
                          title={v.description}
                        >
                          {v.key}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Rich Text Editor */}
                  <div className="space-y-2">
                    <Label className="text-foreground">Template Content *</Label>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <EditorToolbar onCommand={execCommand} onInsertColor={insertColor} />
                      <div
                        ref={editorRef}
                        contentEditable
                        className="min-h-[400px] p-4 bg-white text-black focus:outline-none overflow-auto"
                        style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.6' }}
                        onInput={handleEditorChange}
                        onBlur={handleEditorChange}
                        dangerouslySetInnerHTML={{ __html: formData.content }}
                        data-testid="template-content-editor"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowDialog(false)}
                      className="flex-1 border-border"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={editMode ? handleUpdate : handleCreate}
                      className="flex-1 bg-neon-blue hover:bg-neon-blue/80 text-black"
                      data-testid="save-template-btn"
                    >
                      {editMode ? 'Update Template' : 'Create Template'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Variables Reference */}
          <Card className="bg-card border-border mb-6">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-3">
                <Variable className="w-4 h-4 text-neon-purple" />
                <span className="font-semibold">Available Variables</span>
                {settings?.logo && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 flex items-center gap-1">
                    <Image className="w-3 h-3" /> Logo Available
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {TEMPLATE_VARIABLES.map((v) => (
                  <span
                    key={v.key}
                    className="px-2 py-1 text-xs rounded-full bg-neon-purple/20 text-neon-purple"
                    title={v.description}
                  >
                    {v.key}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-templates-message">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">No Templates Yet</h3>
              <p className="text-muted-foreground mb-4">Create your first contract template with rich formatting</p>
              <Button onClick={openCreateDialog} className="bg-neon-blue hover:bg-neon-blue/80 text-black">
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="templates-list">
              {templates.map((template) => (
                <Card
                  key={template.id}
                  className={`bg-card border-border hover:bg-muted/30 transition-colors ${template.is_default ? 'ring-2 ring-primary' : ''}`}
                  data-testid={`template-item-${template.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                          {template.name}
                          {template.is_default && (
                            <Star className="w-4 h-4 text-neon-blue fill-neon-blue" />
                          )}
                        </CardTitle>
                        <span className="text-xs text-muted-foreground">{template.category}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {template.description && (
                      <p className="text-sm text-muted-foreground mb-3">{template.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mb-4">
                      {template.content?.length || 0} characters • Rich text format
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => previewTemplate(template)}
                        className="text-neon-blue hover:text-neon-blue/80"
                        data-testid={`preview-template-${template.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(template)}
                        data-testid={`edit-template-${template.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDuplicate(template)}
                        data-testid={`duplicate-template-${template.id}`}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Duplicate
                      </Button>
                      {!template.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(template.id)}
                          className="text-neon-purple hover:text-neon-purple/80"
                          data-testid={`set-default-template-${template.id}`}
                        >
                          <Star className="w-4 h-4 mr-1" />
                          Set Default
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        className="text-red-500 hover:text-red-400"
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Preview Dialog */}
          <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
            <DialogContent className="bg-card border border-border max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-foreground">
                  Preview: {selectedTemplate?.name}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Preview with sample data. Variables are replaced with example values.
                </p>
                <div 
                  className="bg-white text-black p-8 rounded-lg"
                  style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.6' }}
                  dangerouslySetInnerHTML={{ 
                    __html: selectedTemplate ? getPreviewContent(selectedTemplate.content) : '' 
                  }}
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
